import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

// lodash
import upperFirst from 'lodash/upperFirst';
import startCase from 'lodash/startCase';
import toUpper from 'lodash/toUpper';
import map from 'lodash/map';

// Multibrand-UI Components
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Pictures from '@zg-rentals/multibrand-ui/lib/icons/Pictures';

// rmw context
import { RMWContext } from 'app/shared/providers/RMWContext';


// constants
import StringConstants from 'app/shared/constants/StringConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import pathsManifest from 'app/shared/pathsManifest';
import numberUtils from 'app/shared/utils/numberUtils';

// utils
import analyticsUtils from 'app/client/utils/analyticsUtils';

import './style.scss';

const propTypes = {
    canAccessApplications: PropTypes.bool.isRequired,
    canAccessPayments: PropTypes.bool.isRequired,
    canAccessContacts: PropTypes.bool.isRequired,
    canAccessAnalytics: PropTypes.bool.isRequired,
    canAccessLeases: PropTypes.bool.isRequired,
    listing: PropTypes.object.isRequired,
    reactRouterLink: PropTypes.func,
};

const defaultProps = {
    reactRouterLink: () => {},
};

const badgeTexts = {
    inactive: 'Off Market',
    active: 'For rent',
    permanent: 'Permanent',
};

const TAB_LABEL = {
    APPLICATIONS: 'Applications',
    MESSAGES: 'Messages',
    LISTING: 'Listing',
    PAYMENTS: 'Payments',
    ANALYTICS: 'Analytics',
    LEASES: 'Leases',
};

const PropertyCard = ({ reactRouterLink, ...props }) => {
    const getContext = () => RMWContext.Consumer._currentValue;

    const getBadgeTextAndColor = () => {
        const { active, permanent } = props.listing;

        let badgeText = badgeTexts.inactive;
        let badgeColor = 'grey';

        if (active) {
            badgeColor = 'green';
            badgeText = permanent ? badgeTexts.permanent : badgeTexts.active;
        }

        return { badgeText, badgeColor };
    };

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const { brand } = getContext();
        const { listing } = props;

        const paths = pathsManifest.getAllPaths(brand);
        const propertyId = `${listing.feedListingId}_${listing.feedId}`;
        const { propertiesPage } = paths;
        const listingPagePath = `${propertiesPage}/${propertyId}/listing`;

        const canGoToEdit = !event.target.className.includes('PropertyCard-link-non-edit');
        if (canGoToEdit) {
            window.router.transitionTo(listingPagePath);
        }
    };

    const formatStreetName = (streetName) => {
        const words = streetName.split(' ');

        return map(words, upperFirst).join(' ');
    };


    const formatStreetAndAddress = (addressComponents) => {
        const {
            street, city, state, unit,
        } = addressComponents;

        // Transformations
        let formattedStreet = street ? formatStreetName(street) : '';
        const formattedCity = city ? startCase(city) : '';
        const formattedState = state ? toUpper(state) : '';
        const formattedUnit = unit ? toUpper(unit) : '';

        if (unit.length) {
            formattedStreet += ` ${formattedUnit}`;
        }

        const formattedAddress = [formattedStreet, formattedCity, formattedState].filter(val => val).join(', ');

        return { formattedStreet, formattedAddress };
    };

    const formatPropertyInfo = (floorPlanDetails) => {
        const {
            numBathrooms,
            numBedrooms,
            squareFeet,
            lowPrice,
        } = floorPlanDetails;

        const fields = [
            ...(numBedrooms ? [`${numBedrooms} beds`] : []),
            ...(numBathrooms ? [`${numBathrooms} baths`] : []),
            ...(squareFeet ? [`${squareFeet} sqft`] : []),
            ...(lowPrice ? [`${numberUtils.asCurrency(lowPrice)}/month`] : []),
        ];

        return (
            <div className="PropertyCard-property-info">
                {
                    fields.map((field, i) => (
                        <span key={field}>
                            {field}
                            {i + 1 < fields.length &&
                                <span className="PropertyCard-field-divider">·</span>
                            }
                        </span>
                    ))
                }
            </div>
        );
    };

    /**
     * Consists of 2 lines:
     *
     * If Multi-family listing:
     *  - First Line: name OR fall back to street name
     *  - Second Line: Full Address
     *
     * If Single-family listing:
     *  - If no property info (ie. beds, bath, sqft, price)
     *      - First line: Street Name
     *      - Second Line: Full Address
     *  - if more than one property info field
     *      - First Line: Full Address
     *      - Second Line: Property Info
     */
    const getPropertyInfoLines = (listingData) => {
        let firstLine;
        let secondLine;

        const {
            street,
            city,
            state,
            unit,
            name,
            isCommunity,
            floorPlanDetails,
        } = listingData;

        const addressComponents = {
            street,
            city,
            state,
            unit,
        };

        const { formattedStreet, formattedAddress } = formatStreetAndAddress(addressComponents);

        if (isCommunity) {
            firstLine = name ? formatStreetName(name) : formattedStreet;
            secondLine = formattedAddress;
        } else {
            const {
                numBathrooms,
                numBedrooms,
                squareFeet,
                lowPrice,
            } = floorPlanDetails;

            if (!numBathrooms && !numBedrooms && !squareFeet && !lowPrice) {
                firstLine = formattedStreet;
                secondLine = formattedAddress;
            } else {
                firstLine = formattedAddress;
                secondLine = formatPropertyInfo(floorPlanDetails);
            }
        }

        return { firstLine, secondLine };
    };

    const getPagePaths = (brand, propertyId) => {
        const {
            propertyDetailsListing,
            propertyDetailsAnalytics,
            propertiesPage,
        } = pathsManifest.getAllPaths(brand);

        const pagePathsRoot = `${propertiesPage}/${propertyId}`;

        return {
            listing: `${pagePathsRoot}/${propertyDetailsListing}`,
            analytics: `${pagePathsRoot}/${propertyDetailsAnalytics}`,
            // standalone apps
            contacts: `/rental-manager/inbox/${propertyId}?byProperty=true&itc=ZRM_MyProps_Contacts_Click`,
            applications: `/rental-manager/applications?propertyId=${propertyId}&itc=ZRM_MyProps_Applications_Click`,
            payments: `/rental-manager/payments/properties/${propertyId}?itc=ZRM_MyProps_Payments_Click`,
            leasing: `/rental-manager/leasing/property/${propertyId}`,
        };
    };

    const getLinkList = (brand, isCommunity) => {
        let linkList = [];
        const {
            listing,
            canAccessApplications,
            canAccessPayments,
            canAccessLeases,
            canAccessContacts,
            canAccessAnalytics,
        } = props;

        const propertyId = `${listing.feedListingId}_${listing.feedId}`;
        const pagePaths = getPagePaths(brand, propertyId);
        const applicationsPaymentsEnabled = canAccessApplications && canAccessPayments;


        if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.hotpads) {
            linkList = [
                {
                    label: TAB_LABEL.LISTING,
                    path: pagePaths.listing,
                },
                {
                    label: TAB_LABEL.MESSAGES,
                    path: pagePaths.contacts,
                    nonEdit: true,
                    disabled: !canAccessContacts,
                },
                {
                    label: TAB_LABEL.ANALYTICS,
                    path: pagePaths.analytics,
                    nonEdit: true,
                    disabled: !isCommunity || !canAccessAnalytics,
                },
            ];
        } else if (brand === StringConstants.UNFORMATTED_BRAND_NAMES.zillow) {
            // if canAccessLeases is true,  remove contacts and add leases instead
            linkList = [
                {
                    label: TAB_LABEL.LISTING,
                    path: pagePaths.listing,
                },
                {
                    label: TAB_LABEL.MESSAGES,
                    path: pagePaths.contacts,
                    nonEdit: true,
                    disabled: canAccessLeases || !canAccessContacts,
                },
                {
                    label: TAB_LABEL.APPLICATIONS,
                    path: pagePaths.applications,
                    nonEdit: true,
                    disabled: !canAccessApplications,
                },
                {
                    label: TAB_LABEL.LEASES,
                    path: pagePaths.leasing,
                    nonEdit: true,
                    disabled: !canAccessLeases,
                },
                {
                    label: TAB_LABEL.PAYMENTS,
                    path: pagePaths.payments,
                    nonEdit: true,
                    disabled: !canAccessPayments,
                },
                {
                    label: TAB_LABEL.ANALYTICS,
                    path: pagePaths.analytics,
                    nonEdit: true,
                    disabled: !isCommunity || !canAccessAnalytics,
                },
            ];
        }

        return { linkList, applicationsPaymentsEnabled };
    };

    // function to log events on GA for payments and application click
    const handleApplicationsLabelClick = (e) => {
        const clickEvent = AnalyticsConstants.GA_EVENTS.PROPERTIES_APPLICATIONS_ROUTE_CLICK;
        props.dispatch(analyticsUtils.track.GAEventV2(clickEvent));

        window.router.updateLocation(e.target.href);
    };

    const handlePaymentsLabelClick = (e) => {
        const clickEvent = AnalyticsConstants.GA_EVENTS.PROPERTIES_PAYMENTS_ROUTE_CLICK;
        props.dispatch(analyticsUtils.track.GAEventV2(clickEvent));

        window.router.updateLocation(e.target.href);
    };

    const handleLeasesClick = (e) => {
        const clickEvent = AnalyticsConstants.GA_EVENTS.PROPERTIES_LEASES_ROUTE_CLICK;
        props.dispatch(analyticsUtils.track.GAEventV2(clickEvent));

        window.router.updateLocation(e.target.href);
    };

    const handleContactsClick = (e) => {
        const clickEvent = AnalyticsConstants.GA_EVENTS.PROPERTIES_CONTACTS_ROUTE_CLICK;
        props.dispatch(analyticsUtils.track.GAEventV2(clickEvent));

        window.router.updateLocation(e.target.href);
    };

    const renderLinks = (isCommunity) => {
        const { brand } = getContext();
        const { linkList, applicationsPaymentsEnabled } = getLinkList(brand, isCommunity);
        let handleLabelClickFunction = event => event.stopPropagation();

        return (
            <StyledContainer
                className={cx(
                    'PropertyCard-links', {
                        'PropertyCard-links-applications-payments-enabled': applicationsPaymentsEnabled,
                    },
                )}
                borderTop={true}
            >
                {linkList.map((link) => {
                    const {
                        nonEdit, label, disabled,
                    } = link;

                    const { path } = link;

                    if (disabled || !label) {
                        return null;
                    }

                    // check to append itc
                    if (label === TAB_LABEL.APPLICATIONS) {
                        handleLabelClickFunction = handleApplicationsLabelClick;
                    }

                    if (label === TAB_LABEL.PAYMENTS) {
                        handleLabelClickFunction = handlePaymentsLabelClick;
                    }

                    if (label === TAB_LABEL.LEASES) {
                        handleLabelClickFunction = handleLeasesClick;
                    }

                    if (label === TAB_LABEL.MESSAGES) {
                        handleLabelClickFunction = handleContactsClick;
                    }

                    const isStandalone = (
                        label === TAB_LABEL.LEASES ||
                        label === TAB_LABEL.PAYMENTS ||
                        label === TAB_LABEL.APPLICATIONS ||
                        label === TAB_LABEL.MESSAGES
                    );

                    return (
                        isStandalone ? (
                            <Link
                                className={cx(
                                    'PropertyCard-link', {
                                        'PropertyCard-link-non-edit': nonEdit,
                                        'PropertyCard-link-applications-payments-enabled': applicationsPaymentsEnabled,
                                    },
                                )}
                                key={label}
                                target="_self"
                                href={path}
                                onClick={handleLabelClickFunction}
                            >
                                {label}
                            </Link>
                        ) : (
                            <Link
                                className={cx(
                                    'PropertyCard-link', {
                                        'PropertyCard-link-non-edit': nonEdit,
                                        'PropertyCard-link-applications-payments-enabled': applicationsPaymentsEnabled,
                                    },
                                )}
                                key={label}
                                reactRouterLink={reactRouterLink}
                                to={path}
                                onClick={handleLabelClickFunction}
                            >
                                {label}
                            </Link>
                        )
                    );
                })}
            </StyledContainer>
        );
    };

    const renderPropertyInfo = () => {
        const { listing } = props;

        const data = {
            street: listing.street,
            city: listing.city,
            state: listing.state,
            unit: listing.unit,
            name: listing.name,
            isCommunity: listing.isCommunity,
            floorPlanDetails: listing.floorPlanDetails,
        };

        const { firstLine, secondLine } = getPropertyInfoLines(data);

        return (
            <div className="PropertyCard-listing-info">
                <Text
                    emphasize={true}
                    type="body"
                    className="PropertyCard-listing-info-line-1"
                >
                    { firstLine }
                </Text>

                <Text
                    type="body-small"
                    className="PropertyCard-listing-info-line-2"
                >
                    { secondLine }
                </Text>
            </div>
        );
    };

    const { isSOH, listing } = props;
    const { primaryPhotoUrl } = listing;

    const imageContainerStyle = primaryPhotoUrl ?
        {
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center center',
            backgroundSize: 'cover',
            backgroundImage: `url(${primaryPhotoUrl.replace('_thumb', '')})`,
        } : {};

    const { badgeText, badgeColor } = getBadgeTextAndColor();

    const classNames = cx('PropertyCard', {
        SOH: isSOH,
    });

    return (
        <StyledContainer
            className={classNames}
            backgroundColor="white"
            borderFull={true}
            borderRounded={true}
            onClick={handleClick}
        >
            <Text
                uppercase={true}
                emphasize={true}
                type="body-tiny"
                className={cx({
                    'PropertyCard-badge': true,
                    'PropertyCard-badge-grey': badgeColor === 'grey',
                    'PropertyCard-badge-green': badgeColor === 'green',
                    'Utils-white': true,
                })}
            >
                { badgeText }
            </Text>

            {
                !primaryPhotoUrl ?
                    (
                        <StyledContainer
                            backgroundColor="lighterGrey"
                            className="PropertyCard-header"
                        >
                            <div className="PropertyCard-icon-container">
                                <Pictures
                                    size="lg"
                                    color="white"
                                />
                            </div>
                        </StyledContainer>
                    ) :
                    (
                        <StyledContainer
                            borderRounded={true}
                            style={imageContainerStyle}
                            className={cx([
                                'PropertyCard-header',
                                'PropertyCard-header-with-image',
                            ])}
                        />
                    )
            }

            <div className="PropertyCard-body">
                { renderPropertyInfo() }
                { renderLinks(listing.isCommunity) }
            </div>
        </StyledContainer>
    );
};

PropertyCard.propTypes = propTypes;
PropertyCard.defaultProps = defaultProps;

export default PropertyCard;
